import { Spacer } from 'app/layouts/generic';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import styles from './mobile.module.css';
import React, { useContext, useEffect, useState } from 'react';
import { useFilesService } from 'hooks/files';
import { SimpleInput } from 'app/shared/input';
import { toast } from 'react-toastify';
import { Checkbox } from 'app/shared/input/checkbox';
import { useMobilePushConfigService } from 'hooks/messaging/mobile/config';
import { PermissionsContext } from 'contexts/permissions';

export const MobilePushConfiguration = () => {
  const { permissions } = useContext(PermissionsContext);
  const { uploadFile } = useFilesService();
  const { fetchConfig, updateConfig } = useMobilePushConfigService();

  // ios config
  const [bundle_id, setBundleId] = useState('');
  const [key_id, setKeyId] = useState('');
  const [p8_file, setP8File] = useState('');
  const [team_id, setTeamId] = useState('');

  // android config
  const [service_account_file, setServiceAccountFile] = useState('');
  const [package_name, setPackageName] = useState('');

  const [is_saving, setIsSaving] = useState(false);
  const [show_android_config, setShowAndroidConfig] = useState(false);
  const [show_ios_config, setShowIOSConfig] = useState(false);

  useEffect(() => {
    fetchConfig().then(({ configs: [config] }) => {
      if (!config) return;
      const { android, ios } = config;
      if (android && (android.package_name || android.service_account_file)) {
        setShowAndroidConfig(true);
        setPackageName(android.package_name);
        setServiceAccountFile(android.service_account_file);
      }

      if (ios && ios.bundle_id) {
        setShowIOSConfig(true);
        setBundleId(ios.bundle_id);
        setKeyId(ios.key_id);
        setP8File(ios.p8_file);
        setTeamId(ios.team_id);
      }
    });
  }, []);

  const handleFileSelection = async (e, config_type) => {
    if (!e.target.files) return;
    const selected_file = e.target.files[0];

    if (e.target.files && selected_file.size <= 200 * 1024) {
      const formData = new FormData();
      formData.append('file', selected_file);

      const { location } = await uploadFile({
        data: formData,
        headers: {
          'Content-Type': 'application/form-data'
        }
      });
      if (config_type === 'ios') {
        setP8File(location);
      } else if (config_type === 'android') {
        setServiceAccountFile(location);
      }
    } else {
      toast.error('File size exceeds the limit of 200KB');
    }
  };

  const submit = async () => {
    const data_to_save = {};
    const errors = {};
    if (show_ios_config) {
      if (p8_file && key_id && team_id && bundle_id) {
        data_to_save.ios = {
          key_id,
          bundle_id,
          p8_file,
          team_id
        };
      } else {
        errors.ios = 'Please fill iOS configuration';
      }
    }

    if (show_android_config) {
      if (package_name && service_account_file) {
        data_to_save.android = { package_name, service_account_file };
      } else {
        errors.android = 'Please fill Android configuration';
      }
    }

    if (errors.ios || errors.android) {
      return toast.error(errors.ios || errors.android);
    }

    setIsSaving(true);
    await updateConfig({ data: data_to_save });
    setIsSaving(false);
  };

  return (
    <div className={styles.wrapper}>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <Checkbox
            checked={show_ios_config}
            label="Show iOS configuration"
            onClick={() => setShowIOSConfig((show) => !show)}
          />
        </GridColumn>
        <GridColumn>
          <Checkbox
            checked={show_android_config}
            label="Show Android configuration"
            onClick={() => setShowAndroidConfig((show) => !show)}
          />
        </GridColumn>
      </GridRow>
      <Spacer multiple={4} />
      {show_ios_config && (
        <>
          <Spacer multiple={4} />
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem title=".p8 file" required>
                <div className="custom-file">
                  <input
                    type="file"
                    accept=".p8"
                    className="custom-file-input"
                    id="p8_file"
                    onChange={(e) => handleFileSelection(e, 'ios')}
                    onClick={(e) => (e.target.value = null)}
                  />
                  <label className="custom-file-label" htmlFor="p8_file">
                    {p8_file ? p8_file.split('/').pop() : 'Select file'}
                  </label>
                </div>
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem title="Bundle ID" required>
                <SimpleInput type="text" value={bundle_id} onInput={setBundleId} />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <DetailItem title="Key ID" required>
                <SimpleInput type="text" value={key_id} onInput={setKeyId} />
              </DetailItem>
            </GridColumn>
            <GridColumn>
              <DetailItem title="Team ID" required>
                <SimpleInput type="text" value={team_id} onInput={setTeamId} />
              </DetailItem>
            </GridColumn>
          </GridRow>
        </>
      )}
      {show_android_config && (
        <>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem title="Package Name" required>
                <SimpleInput type="text" value={package_name} onInput={setPackageName} />
              </DetailItem>
            </GridColumn>
            <GridColumn>
              <DetailItem title="Service Account JSON file" required>
                <div className="custom-file">
                  <input
                    type="file"
                    accept=".json"
                    className="custom-file-input"
                    id="services_file"
                    onChange={(e) => handleFileSelection(e, 'android')}
                    onClick={(e) => (e.target.value = null)}
                  />
                  <label className="custom-file-label" htmlFor="services_file">
                    {service_account_file ? service_account_file.split('/').pop() : 'Select file'}
                  </label>
                </div>
              </DetailItem>
            </GridColumn>
          </GridRow>
          <Spacer multiple={3} />
        </>
      )}
      {(show_android_config || show_ios_config) && (
        <GridRow num_of_columns={2}>
          <GridColumn />
          <GridColumn>
            <Button
              disabled={is_saving || !permissions['mobi_push_config:create']}
              text="Save"
              onClick={submit}
            />
          </GridColumn>
        </GridRow>
      )}
    </div>
  );
};
